/**
 * Uploads / file-drop page (/uploads) — drop a file and it lands in the dev-server inbox.
 * Tell the AI in chat and it moves the file to the right place. Lists recent uploads with
 * who/when/note and the stored path. Uploads as the global identity (uploaded_by).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import InfoModal from '../components/InfoModal';
import { useIdentity, personLabel } from '../context/IdentityContext';
import {
  uploadFile,
  listUploads,
  describeUploadError,
  UploadMetadata,
} from '../services/uploads.service';

const fmtBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (s?: string | null): string => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
};

const Uploads: React.FC = () => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();

  const [uploads, setUploads] = useState<UploadMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  // 0..1 while a big file goes up in parts, so a multi-megabyte upload doesn't look frozen.
  const [progress, setProgress] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUploads(await listUploads(100));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load uploads.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setError(null);
    setFeedback(null);
    const name = file.name;
    try {
      await uploadFile(file, currentPerson, note.trim() || undefined, setProgress);
      setFeedback(`Uploaded "${name}". Tell the AI in chat where it should go.`);
      setFile(null);
      setNote('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e: any) {
      // describeUploadError, not a generic line: a proxy rejection has no JSON `detail`,
      // so reading response.data.detail alone is what produced "Upload failed. Please
      // try again." with no clue what to do about it.
      setError(describeUploadError(e));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    padding: '20px',
  };

  return (
    <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <h1 style={{ color: theme.text, margin: 0 }}>Uploads</h1>
        <InfoModal
          title="Uploads (file drop)"
          body={[
            "Drop a file here and it lands in the dev-server inbox.",
            "Then tell the AI in chat what it is and where it goes — the AI moves the file to the right place (no need to paste contents).",
            "Files are tagged with who uploaded them (your global identity) and an optional note.",
          ]}
        />
      </div>
      <p style={{ color: theme.textSecondary, margin: '4px 0 20px', fontSize: '14px' }}>
        Drop a file here and it lands in the dev-server inbox; tell the AI in chat and it moves the
        file to the right place. Large files are sent in parts, so size is not a problem. Uploading
        as <strong>{personLabel(currentPerson)}</strong>.
      </p>

      {/* Upload form */}
      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => {
            setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
            setFeedback(null);
          }}
          style={{ color: theme.text, fontSize: '14px', marginBottom: '12px', display: 'block' }}
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional — e.g. what this file is / where it should go)"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '9px 12px',
            borderRadius: '6px',
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            color: theme.text,
            fontSize: '14px',
            marginBottom: '12px',
          }}
        />
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          style={{
            padding: '9px 20px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: !file || uploading ? theme.border : theme.primary,
            color: '#fff',
            cursor: !file || uploading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {uploading
            ? progress > 0 && progress < 1
              ? `Uploading… ${Math.round(progress * 100)}%`
              : 'Uploading…'
            : 'Upload to inbox'}
        </button>
        {feedback && <div style={{ color: theme.success, fontSize: '13px', marginTop: '10px' }}>{feedback}</div>}
        {error && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '10px' }}>{error}</div>}
      </div>

      {/* Uploads list */}
      <h2 style={{ color: theme.text, fontSize: '16px', margin: '0 0 12px' }}>Recent uploads</h2>
      {loading ? (
        <div style={{ color: theme.textSecondary }}>Loading uploads…</div>
      ) : uploads.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Nothing in the inbox yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {uploads.map((u) => (
            <div
              key={u.id}
              style={{
                ...cardStyle,
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <strong style={{ color: theme.text, fontSize: '14px', wordBreak: 'break-word' }}>
                  {u.original_filename || u.stored_filename}
                </strong>
                <span style={{ color: theme.textSecondary, fontSize: '12px', whiteSpace: 'nowrap' }}>
                  {personLabel(u.uploaded_by)} · {fmtDate(u.uploaded_at)} · {fmtBytes(u.size)}
                </span>
              </div>
              {u.note && <div style={{ color: theme.text, fontSize: '13px' }}>{u.note}</div>}
              <div style={{ color: theme.textSecondary, fontSize: '12px', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>
                {u.moved_to ? `→ moved to ${u.moved_to}` : u.stored_path}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Uploads;
